import { pickedFile } from '../../utils/pickedFile.js'
import MediaCaptureField from '../common/MediaCaptureField.js'
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import SelectField from '../common/SelectField.js';
import Alert from '../common/Alert.js';
import { DashboardLayout } from '../common/page_component/index.js';
import { testimonialService } from '../../services/testimonialService.js';
import { Plus, Filter, ArrowLeft, Pencil, Package, MoreVertical, HandCoins, XCircle, CreditCard, MessageSquareQuote, Camera} from "lucide-react";
import Button from '../common/Button.js';
import { useTranslation } from 'react-i18next';


const AddTestimonial = () => {
  const [formData, setFormData] = useState({
    full_name: '',
    contact_number: '',
    email: '',
    location: '',
    rating: '5',
    testimonial_text: '',
    is_active: true,
  });
  const [imagePreview, setImagePreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleImageChange = (e) => {
    const file = pickedFile(e);
    if (file) {
      // Validate file size (1MB)
      if (file.size > 1048576) {
        Alert.error('Image size must be less than 1MB');
        e.target.value = '';
        return;
      }

      // Validate file type
      const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        Alert.error('Only PNG, JPG, JPEG, GIF, and WebP images are allowed');
        e.target.value = '';
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const validateForm = () => {
    // Validate contact number (10 digits)
    if (formData.contact_number && !/^\d{10}$/.test(formData.contact_number)) {
      Alert.error('Contact number must be exactly 10 digits');
      return false;
    }

    // Validate email format
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      Alert.error('Please enter a valid email address');
      return false;
    }

    // At least one contact method required
    if (!formData.contact_number && !formData.email) {
      Alert.error('Please provide either contact number or email');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const form = new FormData();
      form.append('full_name', formData.full_name);
      form.append('location', formData.location);
      form.append('rating', formData.rating);
      form.append('testimonial_text', formData.testimonial_text);
      form.append('is_active', formData.is_active);

      // Add contact number if provided
      if (formData.contact_number) {
        form.append('contact_number', formData.contact_number);
      }

      // Add email if provided
      if (formData.email) {
        form.append('email', formData.email);
      }

      if (e.target.image.files[0]) {
        form.append('image', e.target.image.files[0]);
      }

      await testimonialService.createTestimonial(form);
      Alert.success('Testimonial created successfully!');
      setTimeout(() => navigate('/testimonials'), 1500);
    } catch (err) {
      const errorMessage = err.response?.data?.error || 'Failed to create testimonial';
      Alert.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <DashboardLayout>
              <div className="max-w-7xl mx-auto">
                {/* Header */}
                <div className="mb-4 sm:mb-6">
                  <h1 className="text-2xl sm:text-xl font-bold text-secondary flex items-center gap-2 sm:gap-3">
                    <MessageSquareQuote className="w-6 h-6 text-buttonBg" /> Add New Testimonial
                  </h1>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-sm p-4 sm:p-6 md:p-8 space-y-6 sm:space-y-8">
                  {/* Customer Information */}
                  <div>
                    <h3 className="text-base sm:text-md font-semibold text-gray-700 mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
                      Customer Information
                    </h3>
                    <div className="space-y-4 sm:space-y-6">
                      <div>
                        <label className="block text-sm font-bold text-gray-900 mb-2block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Full Name <span class="text-red-500 ml-1">*</span></label>
                        <input
                          type="text"
                          name="full_name"
                          value={formData.full_name}
                          onChange={handleChange}
                          placeholder="Enter customer name"
                          className="w-full px-3 sm:px-4 py-2 text-sm sm:text-base border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400"
                          required
                        />
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                        <div>
                          <label className="block text-sm font-bold text-gray-900 mb-2block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                            Contact Number
                          </label>
                          <input
                            type="text"
                            name="contact_number"
                            value={formData.contact_number}
                            onChange={handleChange}
                            placeholder="Enter 10-digit number"
                            maxLength={10}
                            className="w-full px-3 sm:px-4 py-2 text-sm sm:text-base border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400"
                          />
                          <p className="text-xs text-gray-500 mt-1">Must be exactly 10 digits</p>
                        </div>

                        <div>
                          <label className="block text-sm font-bold text-gray-900 mb-2block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                            Email Address
                          </label>
                          <input
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            placeholder="customer@example.com"
                            className="w-full px-3 sm:px-4 py-2 text-sm sm:text-base border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400"
                          />
                          <p className="text-xs text-gray-500 mt-1">Valid email format</p>
                        </div>
                      </div>

                      <div className="bg-blue-50 border border-blue-200 rounded-xl p-3">
                        <p className="text-xs sm:text-sm text-blue-800">
                          <strong>Note:</strong> At least one contact method (phone or email) is required. 
                          Duplicate testimonials with same contact number or email will not be allowed.
                        </p>
                      </div>

                      <div>
                        <label className="block text-sm font-bold text-gray-900 mb-2block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Location <span class="text-red-500 ml-1">*</span></label>
                        <input
                          type="text"
                          name="location"
                          value={formData.location}
                          onChange={handleChange}
                          placeholder="Enter location/city"
                          className="w-full px-3 sm:px-4 py-2 text-sm sm:text-base border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400"
                          required
                        />
                      </div>

                      <div>
  <label className="block text-sm font-medium text-secondary mb-2">
    Customer Image (Optional)
  </label>

  <MediaCaptureField mode="photo" onPick={handleImageChange} />

  <label
    htmlFor="testimonial-image"
    className="w-40 h-40 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center relative bg-gray-50 hover:bg-gray-100 transition cursor-pointer"
  >
    {!imagePreview ? (
      <div className="text-center">
        <Camera className="mx-auto w-8 h-8 text-gray-400 mb-2" />
        <p className="text-xs text-gray-500">Click to upload</p>
      </div>
    ) : (
      <img
        src={imagePreview}
        alt="Preview"
        className="w-full h-full object-contain rounded-lg"
      />
    )}
  </label>

  <p className="text-xs sm:text-sm text-gray-600 mt-2">
    Max size: 1MB. Formats: PNG, JPG, JPEG, GIF, WebP
  </p>
</div>
                      </div>
                    </div>

                  {/* Testimonial Details */}
                  <div>
                    <h3 className="text-base sm:text-md font-semibold text-gray-700 mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
                      Testimonial Details
                    </h3>
                    <div className="space-y-4 sm:space-y-6">
                      <div>
                        <label className="block text-sm font-bold text-gray-900 mb-2block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Rating <span class="text-red-500 ml-1">*</span></label>
                        <SelectField variant="form"
                          name="rating"
                          value={formData.rating}
                          onChange={handleChange}
                          className="w-full px-3 sm:px-4 py-2 text-sm sm:text-base border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400"
                        >
                          <option value="5">5 ⭐⭐⭐⭐⭐</option>
                          <option value="4">4 ⭐⭐⭐⭐</option>
                          <option value="3">3 ⭐⭐⭐</option>
                          <option value="2">2 ⭐⭐</option>
                          <option value="1">1 ⭐</option>
                        </SelectField>
                      </div>
                      <div>
                        <label className="block text-sm font-bold text-gray-900 mb-2block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Testimonial Text <span class="text-red-500 ml-1">*</span></label>
                        <textarea
                          name="testimonial_text"
                          value={formData.testimonial_text}
                          onChange={handleChange}
                          placeholder="Enter the customer's testimonial"
                          rows={5}
                          className="w-full px-3 sm:px-4 py-2 text-sm sm:text-base border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-bold text-gray-900 mb-2block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Active Status <span class="text-red-500 ml-1">*</span></label>
                        <SelectField variant="form"
                          name="is_active"
                          value={formData.is_active}
                          onChange={handleChange}
                          className="w-full px-3 sm:px-4 py-2 text-sm sm:text-base border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400"
                        >
                          <option value={true}>Active</option>
                          <option value={false}>Inactive</option>
                        </SelectField>
                        <p className="text-xs sm:text-sm text-gray-600 mt-2">Active testimonials will be displayed on the website</p>
                      </div>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 pt-4 sm:pt-6 border-t">
                    <Button variant="primary" type="submit" loading={loading}>
                      {loading ? 'Creating...' : 'Create Testimonial'}
                    </Button>
                    <Button variant="secondary" onClick={() => navigate('/testimonials')}>
                      Cancel
                    </Button>
                  </div>
                </form>
              </div>
    </DashboardLayout>
  );
};

export default AddTestimonial;